import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spacing, Input, Select, FieldGroup, MessageBox, Text } from '@reservamos/elements';
import cybersourceLogo from 'images/payment/cybersource.png';
import { getExpirationMonths, getExpirationYears } from 'utils/formRenderers';
import PaymentImage from '../../ui/atoms/PaymentImage';
import { getPreferredCybersourceCard } from '../../utils/Cybersource';
import useCybersourceMicroformForm from './useCybersourceMicroformForm';
import 'styles/components/CybersourceMicroform';

import useReactNativeMessage from '../../hooks/useReactNativeMessage';

/**
 * CybersourceMicroformForm component.
 * Renders the Cybersource Microform for credit card payments.
 *
 * @param {object} props - Component props.
 * @param {string} props.billingAddress - Optional billing address.
 * @param {Function} props.shouldShowError - Function to determine if a field error should be shown.
 * @param {Function} props.onFieldBlur - Function to mark a field as touched on blur.
 * @param {object} props.formValues - Form values from parent component.
 * @param {Function} props.onFieldChange - Function to update form values.
 * @returns {React.ReactElement} The CybersourceMicroformForm component.
 */
export const CybersourceMicroformForm = ({
  billingAddress,
  shouldShowError = () => true,
  onFieldBlur = () => {},
  formValues = {},
  onFieldChange = () => {},
}) => {
  const { t } = useTranslation('payment');
  const holderNameRef = useRef(null);
  const expirationMonthRef = useRef(null);
  const expirationYearRef = useRef(null);
  const RNMessage = useReactNativeMessage();

  const { createField, initialized, features, binMessageVisible, initError } =
    useCybersourceMicroformForm({
      autoInitialize: true,
    });

  // Non-silent failure: when initialization cannot complete (SDK never ready within the timeout,
  // or an unrecoverable init error), notify the native WebView host through the existing RNMessage
  // channel instead of leaving a blank form. Payload shape is agreed with the native app (see
  // change design): { error: { type, recoverable, reason } }.
  //
  // Contract: exactly once per FAILURE EPISODE. The latch re-arms whenever `initError` clears
  // (recovery / context change sets it back to null), so a later, distinct failure episode is
  // relayed again — while a still-ongoing failure (initError stays set across re-renders) is not
  // re-posted. This keeps the host informed of every episode without spamming within one.
  const initErrorReportedRef = useRef(false);
  useEffect(() => {
    if (!initError) {
      // No active failure (initial or recovered): re-arm so the next episode is reported once.
      initErrorReportedRef.current = false;
      return;
    }
    if (!initErrorReportedRef.current) {
      initErrorReportedRef.current = true;
      RNMessage.post({
        error: {
          type: 'cybersource_form_load_failed',
          recoverable: true,
          reason: initError.reason,
        },
      });
    }
    // RNMessage is recreated each render (unstable ref); depend only on initError so this runs
    // when the failure state changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initError]);

  // Create card number and security code fields
  useEffect(() => {
    if (initialized.microform) {
      // Create card number field
      const cardNumberField = createField({
        fieldType: 'number',
        options: { placeholder: t('card_number') },
        containerId: '#cybersource-number-container',
      });
      // Add event listener for card number field
      cardNumberField.on('change', (data) => {
        const card = getPreferredCybersourceCard(data.card);
        const brand = card?.name || '';
        const cybsCardType = card?.cybsCardType || '';
        onFieldChange('cardNumberValid', data.valid);
        if (brand) onFieldChange('cardBrand', brand);
        if (cybsCardType) onFieldChange('cybsCardType', cybsCardType);
      });

      // Create security code field
      const securityCodeField = createField({
        fieldType: 'securityCode',
        options: { placeholder: t('card_CVV') },
        containerId: '#cybersource-securityCode-container',
      });
      // Add event listener for security code field
      securityCodeField.on('change', (data) => {
        onFieldChange('cardSecurityCodeValid', data.valid);
      });

      RNMessage.post({ formLoaded: true });

      return () => {
        // Flex throws "Field is not loaded" if a hosted field is torn down before its async load()
        // completes; the field is already going away, so ignore that error instead of crashing.
        try {
          cardNumberField?.unload();
        } catch (error) {
          // no-op: field was not loaded yet
        }
        try {
          securityCodeField?.unload();
        } catch (error) {
          // no-op: field was not loaded yet
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized.microform, createField]);

  // Update form values when billing address changes
  useEffect(() => {
    if (billingAddress) {
      onFieldChange('billingAddress', billingAddress);
    }
  }, [billingAddress, onFieldChange]);

  // Removed unused handleChange function

  /**
   * Generate month options for select dropdown
   * @returns {Array} Array of option objects
   */
  const getMonthOptions = () => {
    return getExpirationMonths().map((month) => ({
      value: month.value,
      label: month.value,
    }));
  };

  /**
   * Generate year options for select dropdown
   * @returns {Array} Array of option objects
   */
  const getYearOptions = () => {
    return getExpirationYears().map((year) => ({
      value: year.toString(),
      label: year.toString(),
    }));
  };

  return (
    <div className="card-form">
      <Spacing vertical size="XS">
        {features.BILLING_ADDRESS_ENABLED && billingAddress && (
          <FieldGroup label={t('billing_address')}>
            <Input id="billingAddress" value={billingAddress} disabled />
          </FieldGroup>
        )}

        <label className="flex-label" htmlFor="cybersource-number-container">
          {t('card_number')}
        </label>
        <div
          id="cybersource-number-container"
          className={`flex-microform ${shouldShowError('cardNumber') ? 'has-error' : ''}`}
          onBlur={() => onFieldBlur('cardNumber')}
        />
        <FieldGroup label={t('card_expiration')}>
          <Spacing flexGrow>
            <Select
              ref={expirationMonthRef}
              placeholder={t('card_month')}
              options={getMonthOptions()}
              onChange={(e) => onFieldChange('expirationMonth', e.target.value)}
              onBlur={() => onFieldBlur('expirationMonth')}
              value={formValues.expirationMonth}
              hasError={shouldShowError('expirationMonth')}
              autoComplete="off"
            />
            <Select
              ref={expirationYearRef}
              placeholder={t('card_year')}
              options={getYearOptions()}
              onChange={(e) => onFieldChange('expirationYear', e.target.value)}
              onBlur={() => onFieldBlur('expirationYear')}
              value={formValues.expirationYear}
              hasError={shouldShowError('expirationYear')}
              autoComplete="off"
            />
          </Spacing>
        </FieldGroup>

        <label className="flex-label" htmlFor="cybersource-securityCode-container">
          {t('card_CVV')}
        </label>
        <Spacing alignItems="end">
          <div
            id="cybersource-securityCode-container"
            className={`flex-microform ${shouldShowError('securityCode') ? 'has-error' : ''}`}
            onBlur={() => onFieldBlur('securityCode')}
          />
          <PaymentImage type="cvv" />
        </Spacing>

        <FieldGroup label={t('card_owner_name')}>
          <Input
            ref={holderNameRef}
            id="holderName"
            type="text"
            placeholder={t('card_owner_name')}
            value={formValues.holderName}
            onChange={(e) => onFieldChange('holderName', e.target.value)}
            onBlur={() => onFieldBlur('holderName')}
            hasError={shouldShowError('holderName')}
            autoComplete="off"
          />
        </FieldGroup>

        <Spacing justifyContent="flex-end">
          <img src={cybersourceLogo} alt="Cybersource" height="25" width="110" />
        </Spacing>

        {features.SHOW_DIGITAL_CARD_RECOMMENDATION && binMessageVisible && (
          <MessageBox borderColor="info">
            <Text size="S">{t('digital_card_recommendation')}</Text>
          </MessageBox>
        )}
      </Spacing>
    </div>
  );
};

CybersourceMicroformForm.propTypes = {
  billingAddress: PropTypes.string,
  shouldShowError: PropTypes.func,
  onFieldBlur: PropTypes.func,
  formValues: PropTypes.object,
  onFieldChange: PropTypes.func,
};

export default CybersourceMicroformForm;
